import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { closingSetupActions } from '../actions/closing-setup.actions';
import {
    IAccountingFilePreview,
    IAccountingFileRefusal,
    IClosingSetup,
    IClosingSetupStep,
    IStartClosingRequest,
} from '../models/closing-setup.model';
import type { AccountingFileStatus } from '../reducer/closing-setup.reducer';
import type { ClosingGateDecision } from '../selectors/closing-setup.selectors';
import {
    selectAccountingFilePreview,
    selectAccountingFileRefusal,
    selectAccountingFileStatus,
    selectClosingAccountingReady,
    selectClosingLaunchFailure,
    selectClosingLaunchFinished,
    selectClosingLaunchInFlight,
    selectClosingSetup,
    selectClosingSetupError,
    selectClosingStartError,
    selectClosingSetupSteps,
    selectClosingGate,
    selectClosingStarting,
} from '../selectors/closing-setup.selectors';

/**
 * Facade for the Setup-Wizard slice (REV-21354).
 *
 * The one seam the pages use — nothing else reaches into the store, so the slice's shape stays an
 * internal matter of this library. Reads are METHODS rather than fields, matching
 * `ProjectAccountsProvider` and `ClosingDocumentProvider`: a field initialiser that calls
 * `this.store` depends on the emit order of parameter properties, and no provider here relies on it.
 */
@Injectable()
export class ClosingSetupProvider {
    public constructor(private readonly store: Store) {}

    // ── Reads ────────────────────────────────────────────────────────────────────────────────────


    /**
     * What the navigation gate should do — including the case where the read FAILED, which
     * `getStarted$` cannot express (Code-Review Runde 1, B1).
     */
    public getGate$(): Observable<ClosingGateDecision> {
        return this.store.select(selectClosingGate);
    }

    public getSetup$(): Observable<IClosingSetup | null> {
        return this.store.select(selectClosingSetup);
    }

    /** The launch steps, in render order. */
    public getSteps$(): Observable<readonly IClosingSetupStep[]> {
        return this.store.select(selectClosingSetupSteps);
    }

    /** True once the pipeline has come to rest, whatever the outcome. */
    public getLaunchFinished$(): Observable<boolean> {
        return this.store.select(selectClosingLaunchFinished);
    }

    /** True while a started launch has not come to rest — the overview's import banner is up. */
    public getLaunchInFlight$(): Observable<boolean> {
        return this.store.select(selectClosingLaunchInFlight);
    }

    /** Whether the accounting data is in. The closing's steps stay locked until it is. */
    public getAccountingReady$(): Observable<boolean> {
        return this.store.select(selectClosingAccountingReady);
    }

    /** The step that failed, or `null`. */
    public getLaunchFailure$(): Observable<IClosingSetupStep | null> {
        return this.store.select(selectClosingLaunchFailure);
    }

    public getStarting$(): Observable<boolean> {
        return this.store.select(selectClosingStarting);
    }

    public getError$(): Observable<string | null> {
        return this.store.select(selectClosingSetupError);
    }

    /** Why the START was refused — not the poll. The launch screen's exit hangs off this (R3-1). */
    public getStartError$(): Observable<string | null> {
        return this.store.select(selectClosingStartError);
    }

    /** REV-22007 — where the uploaded trial balance stands. */
    public getAccountingFileStatus$(): Observable<AccountingFileStatus> {
        return this.store.select(selectAccountingFileStatus);
    }

    public getAccountingFilePreview$(): Observable<IAccountingFilePreview | null> {
        return this.store.select(selectAccountingFilePreview);
    }

    public getAccountingFileRefusal$(): Observable<IAccountingFileRefusal | null> {
        return this.store.select(selectAccountingFileRefusal);
    }

    // ── Writes ───────────────────────────────────────────────────────────────────────────────────

    /** The navigation gate's read. Also the launch screen's poll — one call for both. */
    public load(projectId: string): void {
        this.store.dispatch(closingSetupActions.loadSetup({ projectId }));
    }

    public start(projectId: string, request: IStartClosingRequest): void {
        this.store.dispatch(closingSetupActions.startClosing({ projectId, request }));
    }

    /** Called when the launch screen is left, so its poll cannot outlive it. */
    public stopPolling(projectId: string): void {
        this.store.dispatch(closingSetupActions.stopPolling({ projectId }));
    }

    /** REV-22007 — upload a trial balance; the answer lands in the three reads above. */
    public uploadAccountingFile(projectId: string, file: File): void {
        this.store.dispatch(closingSetupActions.uploadAccountingFile({ projectId, file }));
    }

    public resetAccountingFile(projectId: string): void {
        this.store.dispatch(closingSetupActions.resetAccountingFile({ projectId }));
    }
}
